import { useCallback, useEffect, useMemo, useState } from 'react';
import { TENANT } from '@tenant';
import {
  fetchProducts, createProduct, updateProduct, PRODUCT_CATEGORIES, PRODUCT_LIFECYCLES,
  fetchQuotes, fetchQuoteLines, createQuote, setQuoteStatus,
  fetchOrders, fetchOrderLines, updateOrder, createOrder,
  fetchStock, receiveLot, adjustStock, setLotStatus, fefoSuggest,
  fetchShipments, createShipment, setShipmentStatus, fetchLotTrace,
  SHIP_CONDITIONS, SUPPLY_BASES,
  type Product, type Quote, type Order, type OrderLine, type StockRow,
  type Shipment, type FefoSuggestion, type TraceRow,
} from '../lib/trade';
import { fetchAccounts, type Account } from '../lib/customers';
import { onFail } from '../lib/errors';
import './trade.css';

/* =====================================================================================
   Trade: the physical half of quote-to-cash.

   Quotes carry lines and price the ask. An accepted quote becomes an order; confirming
   the order runs the supply-basis gate. Stock is received as lots with expiry dates;
   shipments allocate lots (FEFO suggested, human confirmed) and dispatching one writes
   the stock movements, advances the order and leaves the recall trace. The Trace tab is
   the ISO 13485 question answered in one search: this lot went to these customers.
   ===================================================================================== */

const fmtD = (s: string | null | undefined) => (s ? new Date(s + 'T00:00:00')
  .toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const money = (n: number, c: string) => `${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2 })} ${c}`;
const STATUS_PILL: Record<string, string> = {
  draft: 'mute', sent: 'info', accepted: 'ok', declined: 'warn', expired: 'mute', superseded: 'mute',
  confirmed: 'info', partially_delivered: 'info', delivered: 'ok', invoiced: 'ok', cancelled: 'mute', on_hold: 'warn',
  preparing: 'mute', dispatched: 'info', in_transit: 'info', failed: 'warn', returned: 'warn',
  available: 'ok', quarantined: 'warn', recalled: 'warn', exhausted: 'mute',
};
const pill = (s: string) => <span className={`pill ${STATUS_PILL[s] ?? 'mute'}`}>{s.replace(/_/g, ' ')}</span>;

type Tab = 'products' | 'quotes' | 'orders' | 'stock' | 'shipments' | 'trace';

/* The tenant keeps its books in one currency; a quote or order starts there, not in a
   hardcoded one. */
const BOOK_CCY = (TENANT.brand.baseCurrency ?? 'GBP').toUpperCase();
const CCY_CHOICES = [BOOK_CCY, ...['GBP', 'EUR', 'USD'].filter((c) => c !== BOOK_CCY)];

export function TradeView() {
  const [tab, setTab] = useState<Tab>('quotes');
  const [products, setProducts] = useState<Product[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [stock, setStock] = useState<StockRow[]>([]);
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const [newQuote, setNewQuote] = useState(false);
  const [editProduct, setEditProduct] = useState<Product | 'new' | null>(null);
  const [receiving, setReceiving] = useState(false);
  const [openOrder, setOpenOrder] = useState<Order | null>(null);
  const [shipFor, setShipFor] = useState<Order | null>(null);
  const [newOrder, setNewOrder] = useState(false);
  const [pickingShipOrder, setPickingShipOrder] = useState(false);

  const load = useCallback(() => {
    fetchProducts().then(setProducts).catch(onFail('Products', () => setProducts([])));
    fetchAccounts().then(setAccounts).catch(() => setAccounts([]));
    fetchQuotes().then(setQuotes).catch(() => setQuotes([]));
    fetchOrders().then(setOrders).catch(() => setOrders([]));
    fetchStock().then(setStock).catch(() => setStock([]));
    fetchShipments().then(setShipments).catch(() => setShipments([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const accountName = (id: string) => accounts.find((a) => a.id === id)?.name ?? '';

  return (
    <div className="page trade">
      <div className="page-head">
        <h1 className="page-title">Trade</h1>
        <p className="page-sub">
          Quotes, orders, stock and shipments, with every lot traceable to the customer it
          went to. First expiry ships first; the gate at confirmation keeps supply honest.
        </p>
      </div>

      {msg && <p className="block-note" role="status">{msg}</p>}

      <div className="tabs">
        {(['products', 'quotes', 'orders', 'stock', 'shipments', 'trace'] as Tab[]).map((t) => (
          <button key={t} className={`tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
            {t === 'trace' ? 'Lot trace' : t[0].toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === 'products' && (
        <>
          <div className="toolbar">
            <button className="btn primary" onClick={() => setEditProduct('new')}>New product</button>
          </div>
          <section className="card">
            {products.length === 0 && (
              <p className="module-empty">
                No products yet, and everything downstream starts here: quotes, stock and the
                portal catalogue all read this list.
              </p>
            )}
            {products.map((pr) => (
              <div key={pr.id} className="tr-row" onClick={() => setEditProduct(pr)}>
                <div>
                  <strong>{pr.sku}</strong> · {pr.name}
                  {pill(pr.lifecycle)}
                  {!pr.active && <span className="pill warn">inactive</span>}
                  <div className="cell-sub">
                    {pr.category ?? 'uncategorised'} · per {pr.unit}
                    {Object.keys(pr.regulatory_status ?? {}).length > 0 &&
                      <> · reg: {Object.entries(pr.regulatory_status ?? {}).map(([k, v]) => `${k} ${v}`).join(', ')}</>}
                  </div>
                </div>
                <span className="tr-go">Edit</span>
              </div>
            ))}
          </section>
        </>
      )}

      {tab === 'quotes' && (
        <>
          <div className="toolbar">
            <button className="btn primary" onClick={() => setNewQuote(true)}>New quote</button>
          </div>
          <section className="card">
            {quotes.length === 0 && <p className="module-empty">No quotes yet. The first one takes a minute.</p>}
            {quotes.map((q) => (
              <QuoteRow key={q.id} q={q} accountName={accountName(q.account_id)}
                        account={accounts.find((a) => a.id === q.account_id)}
                        products={products}
                        onChanged={() => { load(); }} onMsg={setMsg} />
            ))}
          </section>
        </>
      )}

      {tab === 'orders' && (
        <>
        <div className="toolbar">
          <button className="btn primary" onClick={() => setNewOrder(true)}>New order</button>
        </div>
        <section className="card">
          {orders.length === 0 && <p className="module-empty">No orders. They arrive from accepted quotes, from the portal, or manually with the button above.</p>}
          {orders.map((o) => (
            <div key={o.id} className="tr-row" onClick={() => setOpenOrder(o)}>
              <div>
                <strong>{o.number}</strong> · {accountName(o.account_id)}
                {o.customer_po_ref && <span className="cell-sub"> · PO {o.customer_po_ref}</span>}
                {pill(o.status)}
                <div className="cell-sub">{fmtD(o.order_date)} · {money(o.total, o.currency)} · {o.supply_basis.toUpperCase().replace('_', '-')}</div>
              </div>
              <span className="tr-go">Open</span>
            </div>
          ))}
        </section>
        </>
      )}

      {tab === 'stock' && (
        <>
          <div className="toolbar">
            <button className="btn primary" onClick={() => setReceiving(true)}>Receive a lot</button>
          </div>
          <section className="card">
            {stock.length === 0 && <p className="module-empty">Nothing on the shelf. Receive the first lot.</p>}
            {stock.map((s) => (
              <StockLine key={s.lot_id} s={s} onChanged={load} onMsg={setMsg} />
            ))}
          </section>
        </>
      )}

      {tab === 'shipments' && (
        <>
        <div className="toolbar">
          <button className="btn primary" onClick={() => setPickingShipOrder(true)}>New shipment</button>
        </div>
        {pickingShipOrder && (
          <section className="card">
            <p className="muted">A shipment fulfils a confirmed order. Pick the order to ship:</p>
            {orders.filter((o) => ['confirmed', 'partially_delivered'].includes(o.status)).length === 0 && (
              <p className="module-empty">No confirmed orders waiting. Confirm an order first (Orders tab).</p>
            )}
            {orders.filter((o) => ['confirmed', 'partially_delivered'].includes(o.status)).map((o) => (
              <div key={o.id} className="tr-row" onClick={() => { setPickingShipOrder(false); setShipFor(o); }}>
                <div><strong>{o.number}</strong> · {accountName(o.account_id)} {pill(o.status)}</div>
                <span className="tr-go">Ship this</span>
              </div>
            ))}
            <button className="link-btn" onClick={() => setPickingShipOrder(false)}>Cancel</button>
          </section>
        )}
        <section className="card">
          {shipments.length === 0 && <p className="module-empty">No shipments yet. They are created from a confirmed order, via the button above or from the order itself.</p>}
          {shipments.map((s) => (
            <ShipmentRow key={s.id} s={s}
                         order={orders.find((o) => o.id === s.order_id)}
                         accountName={accountName(orders.find((o) => o.id === s.order_id)?.account_id ?? '')}
                         onChanged={load} onMsg={setMsg} />
          ))}
        </section>
        </>
      )}

      {tab === 'trace' && <TraceTab />}

      {newOrder && (
        <OrderBuilder products={products} accounts={accounts}
          onClose={() => setNewOrder(false)}
          onDone={(n) => { setNewOrder(false); setMsg(`Order ${n} created as a draft. Open it to confirm when it is right.`); load(); }} />
      )}
      {newQuote && (
        <QuoteBuilder products={products} accounts={accounts}
          onClose={() => setNewQuote(false)}
          onDone={(n) => { setNewQuote(false); setMsg(`Quote ${n} created as a draft. Send it when it is right.`); load(); }} />
      )}
      {openOrder && (
        <OrderDrawer order={openOrder} products={products} accountName={accountName(openOrder.account_id)}
          onClose={() => setOpenOrder(null)}
          onShip={() => { setShipFor(openOrder); setOpenOrder(null); }}
          onChanged={() => { load(); setOpenOrder(null); }} onMsg={setMsg} />
      )}
      {shipFor && (
        <ShipmentBuilder order={shipFor} products={products}
          onClose={() => setShipFor(null)}
          onDone={(n) => { setShipFor(null); setMsg(`Shipment ${n} created. Dispatch it when it leaves the building.`); load(); }} />
      )}
      {editProduct && (
        <ProductForm product={editProduct === 'new' ? null : editProduct}
          onClose={() => setEditProduct(null)}
          onDone={(w) => { setEditProduct(null); setMsg(w); load(); }} />
      )}
      {receiving && (
        <ReceiveLotForm products={products}
          onClose={() => setReceiving(false)}
          onDone={() => { setReceiving(false); setMsg('Lot received into stock.'); load(); }} />
      )}
    </div>
  );
}

function ProductForm({ product, onClose, onDone }: {
  product: Product | null; onClose: () => void; onDone: (msg: string) => void;
}) {
  const [f, setF] = useState({
    sku: product?.sku ?? '', name: product?.name ?? '',
    description: product?.description ?? '', category: product?.category ?? 'kit',
    unit: product?.unit ?? 'kit', lifecycle: product?.lifecycle ?? 'ruo',
    active: product?.active ?? true,
  });
  const [reg, setReg] = useState<{ country: string; status: string }[]>(
    Object.entries(product?.regulatory_status ?? {}).map(([country, status]) => ({ country, status })));
  const [err, setErr] = useState<string | null>(null);
  const set = (k: string) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <div className="drawer tr-drawer">
        <h2>{product ? `Edit ${product.sku}` : 'New product'}</h2>
        {err && <p className="block-note">{err}</p>}
        <div className="tr-two">
          <label className="cf-field"><span>SKU</span>
            <input className="input" value={f.sku} onChange={set('sku')} disabled={!!product} /></label>
          <label className="cf-field"><span>Name</span>
            <input className="input" value={f.name} onChange={set('name')} /></label>
        </div>
        <label className="cf-field"><span>Description (what the customer sees on the portal)</span>
          <input className="input" value={f.description} onChange={set('description')} /></label>
        <div className="tr-two">
          <label className="cf-field"><span>Category</span>
            <select className="input sel" value={f.category} onChange={set('category')}>
              {PRODUCT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select></label>
          <label className="cf-field"><span>Unit sold (test, kit, pack…)</span>
            <input className="input" value={f.unit} onChange={set('unit')} /></label>
        </div>
        <label className="cf-field"><span>Lifecycle (gates supply and the portal catalogue)</span>
          <select className="input sel" value={f.lifecycle} onChange={set('lifecycle')}>
            {PRODUCT_LIFECYCLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select></label>
        <h3 className="tr-h3">Regulatory status by destination</h3>
        <p className="muted">
          Needed before any non-RUO order can be confirmed for that country. ISO-3 country
          and the basis held there (CE-IVD, UKCA, FDA 510(k), registered…).
        </p>
        {reg.map((r, i) => (
          <div key={i} className="tr-lineedit" style={{ gridTemplateColumns: '.6fr 2fr auto' }}>
            <input className="input" value={r.country} maxLength={3} placeholder="GBR"
                   onChange={(e) => setReg(reg.map((x, j) => j === i ? { ...x, country: e.target.value.toUpperCase() } : x))} />
            <input className="input" value={r.status} placeholder="UKCA"
                   onChange={(e) => setReg(reg.map((x, j) => j === i ? { ...x, status: e.target.value } : x))} />
            <button className="link-btn" onClick={() => setReg(reg.filter((_, j) => j !== i))}>×</button>
          </div>
        ))}
        <button className="link-btn" onClick={() => setReg([...reg, { country: '', status: '' }])}>
          + add a destination</button>
        {product && (
          <label className="lv-check" style={{ marginTop: 10 }}>
            <input type="checkbox" checked={f.active}
                   onChange={(e) => setF({ ...f, active: e.target.checked })} />
            <span>Active (available to quote and sell)</span>
          </label>
        )}
        <div className="exp-decide-actions">
          <button className="btn primary" onClick={async () => {
            setErr(null);
            try {
              if (!f.sku.trim() || !f.name.trim()) throw new Error('SKU and name are the minimum.');
              const regStatus = Object.fromEntries(reg.filter((r) => r.country.length === 3 && r.status.trim())
                .map((r) => [r.country, r.status.trim()]));
              if (product) {
                await updateProduct(product.id, { name: f.name.trim(), description: f.description || null,
                  category: f.category, unit: f.unit || 'unit', lifecycle: f.lifecycle,
                  active: f.active, regulatory_status: regStatus });
                onDone(`${product.sku} updated.`);
              } else {
                const id = await createProduct({ sku: f.sku.trim(), name: f.name.trim(),
                  description: f.description || undefined, category: f.category,
                  unit: f.unit || 'unit', lifecycle: f.lifecycle });
                if (Object.keys(regStatus).length) await updateProduct(id, { regulatory_status: regStatus });
                onDone(`${f.sku.trim()} created. It can be quoted and stocked immediately.`);
              }
            } catch (e) { setErr(String((e as Error).message)); }
          }}>{product ? 'Save' : 'Create product'}</button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </>
  );
}

function ReceiveLotForm({ products, onClose, onDone }: {
  products: Product[]; onClose: () => void; onDone: () => void;
}) {
  const [f, setF] = useState({ productId: '', lotNumber: '', expiryDate: '', quantity: '', location: 'Main store', notes: '' });
  const [err, setErr] = useState<string | null>(null);
  const set = (k: string) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <div className="drawer tr-drawer">
        <h2>Receive a lot</h2>
        {err && <p className="block-note">{err}</p>}
        <label className="cf-field"><span>Product</span>
          <select className="input sel" value={f.productId} onChange={set('productId')}>
            <option value="">Choose…</option>
            {products.filter((p) => p.active).map((p) => <option key={p.id} value={p.id}>{p.sku} · {p.name}</option>)}
          </select></label>
        <div className="tr-two">
          <label className="cf-field"><span>Lot number</span>
            <input className="input" value={f.lotNumber} onChange={set('lotNumber')} /></label>
          <label className="cf-field"><span>Expiry date</span>
            <input className="input" type="date" value={f.expiryDate} onChange={set('expiryDate')} /></label>
        </div>
        <div className="tr-two">
          <label className="cf-field"><span>Quantity received</span>
            <input className="input" type="number" min={0.01} step="any" value={f.quantity} onChange={set('quantity')} /></label>
          <label className="cf-field"><span>Location</span>
            <input className="input" value={f.location} onChange={set('location')} /></label>
        </div>
        <label className="cf-field"><span>Notes (CoA reference, condition on arrival…)</span>
          <input className="input" value={f.notes} onChange={set('notes')} /></label>
        <div className="exp-decide-actions">
          <button className="btn primary" onClick={async () => {
            setErr(null);
            try {
              if (!f.productId || !f.lotNumber.trim() || !Number(f.quantity)) {
                throw new Error('Product, lot number and quantity are the minimum.');
              }
              await receiveLot({ productId: f.productId, lotNumber: f.lotNumber.trim(),
                expiryDate: f.expiryDate || undefined, quantity: Number(f.quantity),
                location: f.location || undefined, notes: f.notes || undefined });
              onDone();
            } catch (e) { setErr(String((e as Error).message)); }
          }}>Receive</button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ quotes */
function QuoteRow({ q, accountName, account, products, onChanged, onMsg }: {
  q: Quote; accountName: string; account?: Account; products: Product[];
  onChanged: () => void; onMsg: (s: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [lines, setLines] = useState<import('../lib/trade').QuoteLine[]>([]);
  useEffect(() => { if (open) fetchQuoteLines(q.id).then(setLines).catch(() => setLines([])); }, [open, q.id]);
  return (
    <div className="tr-block">
      <div className="tr-row" onClick={() => setOpen(!open)}>
        <div>
          <strong>{q.number}</strong> · {accountName} {pill(q.status)}
          <div className="cell-sub">
            {fmtD(q.issue_date)}{q.valid_until && <> · valid to {fmtD(q.valid_until)}</>} · {money(q.total, q.currency)}
          </div>
        </div>
        <div className="tr-actions" onClick={(e) => e.stopPropagation()}>
          <button className="btn" disabled={pdfBusy} onClick={async () => {
            setPdfBusy(true);
            try {
              if (!account) throw new Error('The account is still loading; try again.');
              const { openQuotePdf } = await import('../lib/quotepdf');
              const ls = lines.length ? lines : await fetchQuoteLines(q.id);
              await openQuotePdf(q, ls, account, products);
            } catch (e) { onMsg(String((e as Error).message)); }
            finally { setPdfBusy(false); }
          }}>{pdfBusy ? 'Building…' : 'PDF'}</button>
          {q.status === 'draft' && (
            <button className="btn" onClick={() =>
              setQuoteStatus(q.id, 'sent').then(() => { onMsg('Marked as sent. The customer can now accept it on the portal.'); onChanged(); })
                .catch(onFailMsg(onMsg))}>Mark sent</button>
          )}
          {q.status === 'sent' && (
            <button className="link-btn" onClick={() =>
              setQuoteStatus(q.id, 'declined').then(onChanged).catch(onFailMsg(onMsg))}>Declined</button>
          )}
        </div>
      </div>
      {open && lines.length > 0 && (
        <div className="tr-lines">
          {lines.map((l) => (
            <div key={l.id} className="tr-line">
              <span>{l.description || l.product_id}</span>
              <span>{l.quantity} × {l.unit_price}{l.discount_pct > 0 && <> −{l.discount_pct}%</>} = {l.net_amount}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const onFailMsg = (onMsg: (s: string) => void) => (e: Error) => onMsg(e.message);

function QuoteBuilder({ products, accounts, onClose, onDone }: {
  products: Product[]; accounts: Account[]; onClose: () => void; onDone: (number: string) => void;
}) {
  const sellable = products.filter((p) => p.active && !['concept', 'development', 'withdrawn'].includes(p.lifecycle));
  const [accountId, setAccountId] = useState('');
  const [currency, setCurrency] = useState(BOOK_CCY);
  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState([{ productId: '', quantity: 1, unitPrice: 0, discountPct: 0, taxRate: 20 }]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const total = useMemo(() => lines.reduce((s, l) =>
    s + l.quantity * l.unitPrice * (1 - l.discountPct / 100) * (1 + l.taxRate / 100), 0), [lines]);
  const set = (i: number, k: string, v: string | number) =>
    setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <div className="drawer tr-drawer">
        <h2>New quote</h2>
        {err && <p className="block-note">{err}</p>}
        <label className="cf-field"><span>Customer</span>
          <select className="input sel" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
            <option value="">Choose…</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select></label>
        <div className="tr-two">
          <label className="cf-field"><span>Currency</span>
            <select className="input sel" value={currency} onChange={(e) => setCurrency(e.target.value)}>
              {CCY_CHOICES.map((c) => <option key={c}>{c}</option>)}
            </select></label>
          <label className="cf-field"><span>Valid until</span>
            <input className="input" type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} /></label>
        </div>
        <h3 className="tr-h3">Lines</h3>
        <div className="tr-lineedit tr-linehead" aria-hidden="true">
          <span>Product</span><span>Qty</span><span>Unit price</span><span>Disc %</span><span>Tax %</span><span />
        </div>
        {lines.map((l, i) => (
          <div key={i} className="tr-lineedit">
            <select className="input sel" value={l.productId} onChange={(e) => set(i, 'productId', e.target.value)}>
              <option value="">Product…</option>
              {sellable.map((p) => <option key={p.id} value={p.id}>{p.sku} · {p.name}</option>)}
            </select>
            <input className="input" type="number" min={0.01} step="any" value={l.quantity}
                   title="Quantity" onChange={(e) => set(i, 'quantity', Number(e.target.value))} />
            <input className="input" type="number" min={0} step="any" value={l.unitPrice}
                   title="Unit price" onChange={(e) => set(i, 'unitPrice', Number(e.target.value))} />
            <input className="input" type="number" min={0} max={100} value={l.discountPct}
                   title="Discount %" onChange={(e) => set(i, 'discountPct', Number(e.target.value))} />
            <input className="input" type="number" min={0} max={100} value={l.taxRate}
                   title="Tax %" onChange={(e) => set(i, 'taxRate', Number(e.target.value))} />
            {lines.length > 1 && (
              <button className="link-btn" onClick={() => setLines(lines.filter((_, j) => j !== i))}>×</button>
            )}
          </div>
        ))}
        <button className="link-btn" onClick={() =>
          setLines([...lines, { productId: '', quantity: 1, unitPrice: 0, discountPct: 0, taxRate: 20 }])}>
          + another line</button>
        <label className="cf-field"><span>Notes on the quote</span>
          <input className="input" value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
        <p className="muted">Indicative total {total.toLocaleString('en-GB', { maximumFractionDigits: 2 })} {currency} including tax.</p>
        <div className="exp-decide-actions">
          <button className="btn primary" disabled={busy} onClick={async () => {
            setBusy(true); setErr(null);
            try {
              if (!accountId) throw new Error('Choose the customer.');
              const good = lines.filter((l) => l.productId && l.quantity > 0);
              if (!good.length) throw new Error('At least one line needs a product and a quantity.');
              await createQuote({ accountId, currency, validUntil: validUntil || undefined,
                notes: notes || undefined, lines: good });
              onDone('created');
            } catch (e) { setErr(String((e as Error).message)); setBusy(false); }
          }}>Create draft</button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </>
  );
}

function OrderBuilder({ products, accounts, onClose, onDone }: {
  products: Product[]; accounts: Account[]; onClose: () => void; onDone: (number: string) => void;
}) {
  const sellable = products.filter((p) => p.active && !['concept', 'development', 'withdrawn'].includes(p.lifecycle));
  const [accountId, setAccountId] = useState('');
  const [currency, setCurrency] = useState(BOOK_CCY);
  const [basis, setBasis] = useState('ruo');
  const [poRef, setPoRef] = useState('');
  const [country, setCountry] = useState('');
  const [address, setAddress] = useState('');
  const [lines, setLines] = useState([{ productId: '', quantity: 1, unitPrice: 0, discountPct: 0, taxRate: 20 }]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (i: number, k: string, v: string | number) =>
    setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <div className="drawer tr-drawer">
        <h2>New order (manual)</h2>
        <p className="muted">For orders arriving by phone, email or tender: same gates, no quote needed.</p>
        {err && <p className="block-note">{err}</p>}
        <label className="cf-field"><span>Customer</span>
          <select className="input sel" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
            <option value="">Choose…</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select></label>
        <div className="tr-two">
          <label className="cf-field"><span>Currency</span>
            <select className="input sel" value={currency} onChange={(e) => setCurrency(e.target.value)}>
              {CCY_CHOICES.map((cur) => <option key={cur}>{cur}</option>)}
            </select></label>
          <label className="cf-field"><span>Supply basis</span>
            <select className="input sel" value={basis} onChange={(e) => setBasis(e.target.value)}>
              {SUPPLY_BASES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select></label>
        </div>
        <div className="tr-two">
          <label className="cf-field"><span>Customer PO reference</span>
            <input className="input" value={poRef} onChange={(e) => setPoRef(e.target.value)} /></label>
          <label className="cf-field"><span>Destination country (ISO-3)</span>
            <input className="input" maxLength={3} value={country} placeholder="GBR"
                   onChange={(e) => setCountry(e.target.value.toUpperCase())} /></label>
        </div>
        <label className="cf-field"><span>Delivery address</span>
          <input className="input" value={address} onChange={(e) => setAddress(e.target.value)} /></label>
        <h3 className="tr-h3">Lines</h3>
        <div className="tr-lineedit tr-linehead" aria-hidden="true">
          <span>Product</span><span>Qty</span><span>Unit price</span><span>Disc %</span><span>Tax %</span><span />
        </div>
        {lines.map((l, i) => (
          <div key={i} className="tr-lineedit">
            <select className="input sel" value={l.productId} onChange={(e) => set(i, 'productId', e.target.value)}>
              <option value="">Product…</option>
              {sellable.map((p) => <option key={p.id} value={p.id}>{p.sku} · {p.name}</option>)}
            </select>
            <input className="input" type="number" min={0.01} step="any" value={l.quantity}
                   onChange={(e) => set(i, 'quantity', Number(e.target.value))} />
            <input className="input" type="number" min={0} step="any" value={l.unitPrice}
                   onChange={(e) => set(i, 'unitPrice', Number(e.target.value))} />
            <input className="input" type="number" min={0} max={100} value={l.discountPct}
                   onChange={(e) => set(i, 'discountPct', Number(e.target.value))} />
            <input className="input" type="number" min={0} max={100} value={l.taxRate}
                   onChange={(e) => set(i, 'taxRate', Number(e.target.value))} />
            {lines.length > 1 && (
              <button className="link-btn" onClick={() => setLines(lines.filter((_, j) => j !== i))}>×</button>
            )}
          </div>
        ))}
        <button className="link-btn" onClick={() =>
          setLines([...lines, { productId: '', quantity: 1, unitPrice: 0, discountPct: 0, taxRate: 20 }])}>
          + another line</button>
        <div className="exp-decide-actions">
          <button className="btn primary" disabled={busy} onClick={async () => {
            setBusy(true); setErr(null);
            try {
              if (!accountId) throw new Error('Choose the customer.');
              const good = lines.filter((l) => l.productId && l.quantity > 0);
              if (!good.length) throw new Error('At least one line needs a product and a quantity.');
              const n = await createOrder({ accountId, currency, supplyBasis: basis,
                customerPoRef: poRef || undefined, shipToCountry: country || undefined,
                shipToAddress: address || undefined, lines: good });
              onDone(n);
            } catch (e) { setErr(String((e as Error).message)); setBusy(false); }
          }}>Create draft order</button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ orders */
function OrderDrawer({ order, products, accountName, onClose, onShip, onChanged, onMsg }: {
  order: Order; products: Product[]; accountName: string; onClose: () => void;
  onShip: () => void; onChanged: () => void; onMsg: (s: string) => void;
}) {
  const [lines, setLines] = useState<OrderLine[]>([]);
  const [basis, setBasis] = useState(order.supply_basis);
  const [country, setCountry] = useState(order.ship_to?.country ?? '');
  const [address, setAddress] = useState(order.ship_to?.address ?? '');
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { fetchOrderLines(order.id).then(setLines).catch(() => setLines([])); }, [order.id]);
  const productName = (id: string) => {
    const p = products.find((x) => x.id === id); return p ? `${p.sku} · ${p.name}` : id;
  };
  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <div className="drawer tr-drawer">
        <h2>{order.number} · {accountName} {pill(order.status)}</h2>
        {err && <p className="block-note">{err}</p>}
        {order.customer_po_ref && <p className="muted">Customer PO: {order.customer_po_ref}</p>}
        <div className="tr-lines">
          {lines.map((l) => (
            <div key={l.id} className="tr-line">
              <span>{l.description || productName(l.product_id)}</span>
              <span>{l.quantity_delivered}/{l.quantity} delivered</span>
            </div>
          ))}
        </div>
        <div className="tr-two">
          <label className="cf-field"><span>Supply basis</span>
            <select className="input sel" value={basis} onChange={(e) => setBasis(e.target.value)}
                    disabled={order.status !== 'draft'}>
              {SUPPLY_BASES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select></label>
          <label className="cf-field"><span>Destination country (ISO-3)</span>
            <input className="input" value={country} maxLength={3} placeholder="GBR"
                   onChange={(e) => setCountry(e.target.value.toUpperCase())}
                   disabled={order.status !== 'draft'} /></label>
        </div>
        <label className="cf-field"><span>Delivery address</span>
          <input className="input" value={address} onChange={(e) => setAddress(e.target.value)}
                 disabled={!['draft', 'confirmed'].includes(order.status)} /></label>
        <div className="exp-decide-actions">
          {order.status === 'draft' && (
            <button className="btn primary" onClick={async () => {
              setErr(null);
              try {
                await updateOrder(order.id, { supply_basis: basis,
                  ship_to: { ...order.ship_to, country, address }, status: 'confirmed' } as Partial<Order>);
                onMsg(`${order.number} confirmed. Create the shipment when stock is allocated.`);
                onChanged();
              } catch (e) { setErr(String((e as Error).message)); }
            }}>Confirm order</button>
          )}
          {['confirmed', 'partially_delivered'].includes(order.status) && (
            <button className="btn primary" onClick={onShip}>New shipment</button>
          )}
          {order.status === 'draft' && (
            <button className="btn" onClick={() =>
              updateOrder(order.id, { status: 'cancelled' } as Partial<Order>)
                .then(onChanged).catch((e) => setErr(e.message))}>Cancel order</button>
          )}
          <button className="btn" onClick={onClose}>Done</button>
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ stock */
function StockLine({ s, onChanged, onMsg }: { s: StockRow; onChanged: () => void; onMsg: (m: string) => void }) {
  const expSoon = s.expiry_date && new Date(s.expiry_date) < new Date(Date.now() + 60 * 864e5);
  return (
    <div className="tr-row">
      <div>
        <strong>{s.sku}</strong> · lot {s.lot_number} {pill(s.status)}
        <div className="cell-sub">
          {s.product_name} · <strong>{s.on_hand}</strong> on hand · {s.location}
          {s.expiry_date && (
            <span className={expSoon ? 'tr-exp-soon' : ''}> · expires {fmtD(s.expiry_date)}</span>
          )}
        </div>
      </div>
      <div className="tr-actions">
        {s.status === 'available' && s.on_hand > 0 && (
          <button className="link-btn" onClick={() => {
            const n = window.prompt(`Scrap how many of lot ${s.lot_number}? (${s.on_hand} on hand)`);
            if (!n) return;
            const why = window.prompt('Reason for the write-off:') ?? '';
            adjustStock(s.lot_id, 'scrap', Number(n), why)
              .then(() => { onMsg('Written off.'); onChanged(); }).catch((e) => onMsg(e.message));
          }}>Scrap</button>
        )}
        {s.status === 'available' && (
          <button className="link-btn" onClick={() =>
            setLotStatus(s.lot_id, 'quarantined').then(onChanged).catch((e) => onMsg(e.message))}>Quarantine</button>
        )}
        {s.status === 'quarantined' && (
          <button className="link-btn" onClick={() =>
            setLotStatus(s.lot_id, 'available').then(onChanged).catch((e) => onMsg(e.message))}>Release</button>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ shipments */
function ShipmentRow({ s, order, accountName, onChanged, onMsg }: {
  s: Shipment; order?: Order; accountName: string; onChanged: () => void; onMsg: (m: string) => void;
}) {
  return (
    <div className="tr-row">
      <div>
        <strong>{s.number}</strong> · {order?.number} · {accountName} {pill(s.status)}
        <div className="cell-sub">
          {SHIP_CONDITIONS.find(([k]) => k === s.condition)?.[1]}
          {s.courier && <> · {s.courier}</>}
          {s.tracking_ref && (
            s.tracking_url
              ? <> · <a className="cg-filelink" href={s.tracking_url} target="_blank" rel="noreferrer">{s.tracking_ref} ↗</a></>
              : <> · {s.tracking_ref}</>
          )}
          {s.dispatched_on && <> · dispatched {fmtD(s.dispatched_on)}</>}
          {s.delivered_on && <> · delivered {fmtD(s.delivered_on)}</>}
          {s.excursion && <span className="pill warn">temperature excursion</span>}
        </div>
      </div>
      <div className="tr-actions">
        {s.status === 'preparing' && (
          <button className="btn primary" onClick={() =>
            setShipmentStatus(s.id, { status: 'dispatched' })
              .then(() => { onMsg(`${s.number} dispatched: stock moved, order updated.`); onChanged(); })
              .catch((e) => onMsg(e.message))}>Dispatch</button>
        )}
        {['dispatched', 'in_transit'].includes(s.status) && (
          <button className="btn" onClick={() =>
            setShipmentStatus(s.id, { status: 'delivered' })
              .then(onChanged).catch((e) => onMsg(e.message))}>Mark delivered</button>
        )}
      </div>
    </div>
  );
}

function ShipmentBuilder({ order, products, onClose, onDone }: {
  order: Order; products: Product[]; onClose: () => void; onDone: (n: string) => void;
}) {
  const [lines, setLines] = useState<OrderLine[]>([]);
  const [allocs, setAllocs] = useState<Record<string, FefoSuggestion[]>>({});
  const [courier, setCourier] = useState('');
  const [trackingRef, setTrackingRef] = useState('');
  const [trackingUrl, setTrackingUrl] = useState('');
  const [condition, setCondition] = useState('dry_ice');
  const [logger, setLogger] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    fetchOrderLines(order.id).then(async (ls) => {
      const open = ls.filter((l) => l.quantity_delivered < l.quantity);
      setLines(open);
      const a: Record<string, FefoSuggestion[]> = {};
      for (const l of open) {
        a[l.id] = await fefoSuggest(l.product_id, l.quantity - l.quantity_delivered).catch(() => []);
      }
      setAllocs(a);
    }).catch((e) => setErr(e.message));
  }, [order.id]);

  const productName = (id: string) => {
    const p = products.find((x) => x.id === id); return p ? p.sku : id;
  };

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <div className="drawer tr-drawer">
        <h2>Ship {order.number}</h2>
        {err && <p className="block-note">{err}</p>}
        <h3 className="tr-h3">Allocation (first expiry first; edit if you must)</h3>
        {lines.map((l) => (
          <div key={l.id} className="tr-allocblock">
            <div className="tr-allochead">{productName(l.product_id)} · {l.quantity - l.quantity_delivered} outstanding</div>
            {(allocs[l.id] ?? []).length === 0 && (
              <p className="muted">No stock available for this line. Receive a lot first.</p>
            )}
            {(allocs[l.id] ?? []).map((a, i) => (
              <div key={a.lot_id} className="tr-line">
                <span>lot {a.lot_number}{a.expiry_date && <> · exp {fmtD(a.expiry_date)}</>}</span>
                <input className="input tr-qty" type="number" min={0} step="any" value={a.take}
                       onChange={(e) => setAllocs({ ...allocs,
                         [l.id]: allocs[l.id].map((x, j) => j === i ? { ...x, take: Number(e.target.value) } : x) })} />
              </div>
            ))}
          </div>
        ))}
        <div className="tr-two">
          <label className="cf-field"><span>Courier</span>
            <input className="input" value={courier} placeholder="DHL, UPS, Biocair…"
                   onChange={(e) => setCourier(e.target.value)} /></label>
          <label className="cf-field"><span>Condition</span>
            <select className="input sel" value={condition} onChange={(e) => setCondition(e.target.value)}>
              {SHIP_CONDITIONS.map(([k, lab]) => <option key={k} value={k}>{lab}</option>)}
            </select></label>
        </div>
        <div className="tr-two">
          <label className="cf-field"><span>Tracking reference</span>
            <input className="input" value={trackingRef} onChange={(e) => setTrackingRef(e.target.value)} /></label>
          <label className="cf-field"><span>Tracking link</span>
            <input className="input" value={trackingUrl} placeholder="https://…"
                   onChange={(e) => setTrackingUrl(e.target.value)} /></label>
        </div>
        <label className="cf-field"><span>Temperature logger ID (cold chain)</span>
          <input className="input" value={logger} onChange={(e) => setLogger(e.target.value)} /></label>
        <div className="exp-decide-actions">
          <button className="btn primary" disabled={busy} onClick={async () => {
            setBusy(true); setErr(null);
            try {
              const shipLines = lines.flatMap((l) =>
                (allocs[l.id] ?? []).filter((a) => a.take > 0)
                  .map((a) => ({ orderLineId: l.id, lotId: a.lot_id, quantity: a.take })));
              if (!shipLines.length) throw new Error('Nothing allocated. Set a quantity against at least one lot.');
              await createShipment({ orderId: order.id, courier, trackingRef, trackingUrl,
                condition, tempLoggerId: logger, shipTo: order.ship_to, lines: shipLines });
              onDone('created');
            } catch (e) { setErr(String((e as Error).message)); setBusy(false); }
          }}>Create shipment</button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ trace */
function TraceTab() {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<TraceRow[]>([]);
  const [searched, setSearched] = useState(false);
  return (
    <section className="card">
      <p className="muted">
        The recall question: type a lot number and see every customer, shipment and date it
        reached. Leave it blank to list everything shipped.
      </p>
      <div className="toolbar">
        <input className="input tr-tracein" placeholder="Lot number…" value={q}
               onChange={(e) => setQ(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter') { fetchLotTrace(q || undefined).then((r) => { setRows(r); setSearched(true); }); } }} />
        <button className="btn primary" onClick={() =>
          fetchLotTrace(q || undefined).then((r) => { setRows(r); setSearched(true); })}>Trace</button>
      </div>
      {searched && rows.length === 0 && <p className="module-empty">Nothing shipped matches.</p>}
      {rows.map((r, i) => (
        <div key={i} className="tr-row">
          <div>
            <strong>{r.lot_number}</strong> · {r.sku} → {r.account_name}
            <div className="cell-sub">
              {r.quantity} units · {r.shipment_number} · {r.order_number}
              {r.dispatched_on && <> · {fmtD(r.dispatched_on)}</>}
              {r.courier && <> · {r.courier} {r.tracking_ref}</>}
            </div>
          </div>
        </div>
      ))}
    </section>
  );
}
